import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  Grid,
  Avatar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import agent from "../../utils/axios";
import LoadingSpinner from "../Loading/LoadingSpinner";

export interface CSITipaltiStagings {
  id: string;
  payeeId: string;
  tin: string;
  groupPayeeIds: string[];
  matchedPayeeId: string | null;
  additionalMatchedPayeeIds: string[];
  csiPaymentMethod: string;
  csiCreatedDate: string;
  csiName: string;
  csiEmail: string;
  csiAddress: string;
  tipaltiName: string;
  tipaltiEmail: string;
  tipaltiAddress: string;
  tipaltiPaymentMethod: string;
  inTipalti: boolean | null;
  isMatchedWithTipalti: boolean | null;
  matchBasis: string;
  migratedBills: number;
  nonMigratedBills: number;
  analysisCreatedOn: string;
  analysisCompletedOn: string | null;
  analysisCompletedBy: string;
  isPayable: boolean | null;
  payableReason: string;
  memo: string | null;
  lastModified: string | null;
  lastModifiedBy: string | null;
  reviewedResults: string | null;
  isActive: boolean;
}

// Styled components
const DialogStyled = styled(Dialog)<{ zIndex?: number }>`
  .MuiDialog-paper {
    border-radius: 12px;
    padding: 16px;
    max-width: 600px;
    z-index: ${({ zIndex }) => zIndex || 1300};
    background-color: ${({ theme }) => theme.palette.background.paper};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;
const SectionHeader = styled(Typography)`
  font-weight: 600;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.palette.primary.main};
`;
const Button = styled(MuiButton)`
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.dark};
  }
`;
const StatusChip = styled(Chip)`
  height: 24px;
  font-size: 0.75rem;
  font-weight: bold;
  margin-left: 8px;
`;
const ClickableSpan = styled("span")`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  &:hover {
    text-decoration: underline;
  }
`;

const getStatusDescription = (value: boolean | null, statusType: string) => {
  const statusValue =
    value === true ? "true" : value === false ? "false" : "null";

  switch (statusType) {
    case "PAYABLE":
      return `Payable status: ${statusValue} - Indicates if this payee is eligible to receive payments`;
    case "ACTIVE":
      return `Active status: ${statusValue} - Indicates if this payee record is currently active in the system`;
    case "IN TIPALTI":
      return `In Tipalti status: ${statusValue} - Indicates if this payee exists in the Tipalti payment system`;
    case "MATCHED":
      return `Matched status: ${statusValue} - Indicates if this CSI payee has been matched with a Tipalti payee record`;
    default:
      return `${statusType} status is ${statusValue}`;
  }
};

const getGroupPayeeIdsColor = (groupPayeeIds: string[]) => {
  if (!groupPayeeIds || groupPayeeIds.length === 0) return "yellow";
  if (groupPayeeIds.length === 1) return "green";
  return "red";
};

const getGroupPayeeIdsDescription = (groupPayeeIds: string[]) => {
  const count = groupPayeeIds ? groupPayeeIds.length : 0;
  return `Group Payee IDs count: ${count} - Shows the number of payee IDs grouped together. Green for 1 ID (good), Red for multiple IDs (needs review), Yellow for none`;
};

export function PayeeModal({
  item,
  open,
  onClose,
}: {
  item: CSITipaltiStagings | null;
  open: boolean;
  onClose: () => void;
}) {
  const [editMode, setEditMode] = useState(false);
  const [memo, setMemo] = useState("");
  const [reviewedResults, setReviewedResults] = useState("");
  const [isActive, setIsActive] = useState<boolean>(true);

  // Initialize local state when item prop changes
  useEffect(() => {
    if (item) {
      setMemo(item.memo || "");
      setReviewedResults(item.reviewedResults || "");
      setIsActive(item.isActive ?? true);
    }
  }, [item?.id, item?.memo, item?.reviewedResults, item?.isActive]);

  const handleSave = async () => {
    if (!item) return;
    const now = new Date().toISOString();
    const updatedItem = {
      ...item,
      memo: memo,
      reviewedResults: reviewedResults,
      lastModified: now,
      lastModifiedBy: "currentUser", // Replace with actual user
      isActive: isActive,
    };

    try {
      await agent.CSITipalti.updateCSITipalti(item.id, updatedItem);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating payee item:", error);
    }
  };

  const getColor = (value: boolean | null) => {
    if (value === true) return "green";
    if (value === false) return "red";
    return "gold";
  };

  const formatTin = (tin: string) => {
    return tin.length > 10 ? `...${tin.slice(-10)}` : tin;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
  };

  if (!item) return null;

  return (
    <DialogStyled open={open} onClose={onClose} zIndex={1400}>
      <DialogTitle sx={{ fontWeight: 500 }}>{item.csiName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* CSI Information Section */}
          <Box>
            <SectionHeader variant="subtitle1">CSI Information</SectionHeader>
            <Typography variant="body2">Name: {item.csiName}</Typography>
            <Typography variant="body2">Email: {item.csiEmail}</Typography>
            <Typography variant="body2">Address: {item.csiAddress}</Typography>
            <Typography variant="body2">
              Payment Method: {item.csiPaymentMethod || "none"}
            </Typography>
            <Typography variant="body2">
              Created Date: {formatDate(item.csiCreatedDate)}
            </Typography>
          </Box>

          {/* Tipalti Information Section */}
          <Box>
            <SectionHeader variant="subtitle1">
              Tipalti Information
            </SectionHeader>
            <Typography variant="body2">Name: {item.tipaltiName}</Typography>
            <Typography variant="body2">Email: {item.tipaltiEmail}</Typography>
            <Typography variant="body2">
              Address: {item.tipaltiAddress}
            </Typography>
            <Typography variant="body2">
              Payment Method: {item.tipaltiPaymentMethod || "none"}
            </Typography>
          </Box>

          {/* Payee Details Section */}
          <Box>
            <SectionHeader variant="subtitle1">Payee Details</SectionHeader>
            <Typography variant="body2">Payee ID: {item.payeeId}</Typography>
            <Typography variant="body2">TIN: {formatTin(item.tin)}</Typography>
            <Typography variant="body2">
              Matched Payee ID: {item.matchedPayeeId || "null"}
            </Typography>
            <Typography variant="body2">
              Group Payee IDs:{" "}
              {Array.isArray(item.groupPayeeIds)
                ? item.groupPayeeIds.join(", ") || "none"
                : "none"}
            </Typography>
            <Typography variant="body2">
              Additional Matched Payee IDs:{" "}
              {Array.isArray(item.additionalMatchedPayeeIds)
                ? item.additionalMatchedPayeeIds.join(", ") || "none"
                : "none"}
            </Typography>
          </Box>

          {/* Analysis Details Section */}
          <Box>
            <SectionHeader variant="subtitle1">Analysis Details</SectionHeader>
            <Typography variant="body2">
              In Tipalti:{" "}
              <span style={{ color: getColor(item.inTipalti) }}>
                {item.inTipalti != null ? item.inTipalti.toString() : "null"}
              </span>
            </Typography>
            <Typography variant="body2">
              Is Matched With Tipalti:{" "}
              <span style={{ color: getColor(item.isMatchedWithTipalti) }}>
                {item.isMatchedWithTipalti != null
                  ? item.isMatchedWithTipalti.toString()
                  : "null"}
              </span>
            </Typography>
            <Typography variant="body2">
              Match Basis: {item.matchBasis}
            </Typography>
            <Typography variant="body2">
              Migrated Bills: {item.migratedBills}
            </Typography>
            <Typography variant="body2">
              Non-Migrated Bills: {item.nonMigratedBills}
            </Typography>
            <Typography variant="body2">
              Analysis Completed By: {item.analysisCompletedBy}
            </Typography>
            <Typography variant="body2">
              Analysis Created On: {formatDate(item.analysisCreatedOn)}
            </Typography>
            <Typography variant="body2">
              Analysis Completed On:{" "}
              {item.analysisCompletedOn
                ? formatDate(item.analysisCompletedOn)
                : "N/A"}
            </Typography>
            <Typography variant="body2">
              Is Payable:{" "}
              <span style={{ color: getColor(item.isPayable) }}>
                {item.isPayable != null ? item.isPayable.toString() : "null"}
              </span>
            </Typography>
            <Typography variant="body2">
              Payable Reason: {item.payableReason || "none"}
            </Typography>
          </Box>

          {/* Review Information Section */}
          <Box>
            <SectionHeader variant="subtitle1">
              Review Information
            </SectionHeader>
            {editMode ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  size="small"
                />
                <Select
                  value={reviewedResults}
                  onChange={(e) => setReviewedResults(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="No Further Action">
                    No Further Action
                  </MenuItem>
                  <MenuItem value="Need Additional Information">
                    Need Additional Information
                  </MenuItem>
                  <MenuItem value="Waiting on Applicant">
                    Waiting on Applicant
                  </MenuItem>
                  <MenuItem value="Duplicate Payee Record">
                    Duplicate Payee Record
                  </MenuItem>
                  <MenuItem value="Pending Review">Pending Review</MenuItem>
                  <MenuItem value="On Hold">On Hold</MenuItem>
                </Select>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  }
                  label="Active"
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                  <Button variant="outlined" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2">
                  Memo: {item.memo || "none"}
                </Typography>
                <Typography variant="body2">
                  Reviewed Results: {item.reviewedResults || "none"}
                </Typography>
                <Typography variant="body2">
                  Last Modified:{" "}
                  {item.lastModified
                    ? formatDateTime(item.lastModified)
                    : "N/A"}
                </Typography>
                <Typography variant="body2">
                  Last Modified By: {item.lastModifiedBy || "N/A"}
                </Typography>
                <Typography variant="body2">
                  Active: {item.isActive.toString()}
                </Typography>
                <Button variant="outlined" onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </DialogStyled>
  );
}

export function TinMatchModal({
  tin,
  items,
  open,
  onClose,
  onPayeeIdClick,
}: {
  tin: string;
  items: CSITipaltiStagings[];
  open: boolean;
  onClose: () => void;
  onPayeeIdClick: (payeeId: string) => void;
}) {
  const matchingItems = items.filter((item) => item.tin === tin);

  const getColor = (value: boolean | null) => {
    if (value === true) return "green";
    if (value === false) return "red";
    return "gold";
  };

  const getValueColor = (value: boolean | null) => {
    if (value === true) return "#fff";
    if (value === false) return "#fff";
    return "#000";
  };

  return (
    <DialogStyled open={open} onClose={onClose} zIndex={1300}>
      <DialogTitle sx={{ fontWeight: 500 }}>
        Records with Matching TIN
      </DialogTitle>
      <DialogContent>
        {matchingItems.length === 0 ? (
          <Typography variant="body2">
            No records found with matching TIN.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {matchingItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Typography variant="body2">Name: {item.csiName}</Typography>
                  <Typography variant="body2">
                    Payee ID:{" "}
                    <ClickableSpan
                      onClick={() => onPayeeIdClick(item.payeeId)}
                      aria-label="View payee details"
                    >
                      {item.payeeId}
                    </ClickableSpan>
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Tooltip
                    title={getStatusDescription(item.isPayable, "PAYABLE")}
                  >
                    <StatusChip
                      label="PAYABLE"
                      style={{
                        backgroundColor: getColor(item.isPayable),
                        color: getValueColor(item.isPayable),
                      }}
                      aria-label={getStatusDescription(
                        item.isPayable,
                        "PAYABLE"
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title={getStatusDescription(item.isActive, "ACTIVE")}
                  >
                    <StatusChip
                      label="ACTIVE"
                      style={{
                        backgroundColor: getColor(item.isActive),
                        color: "#fff",
                      }}
                      aria-label={getStatusDescription(item.isActive, "ACTIVE")}
                    />
                  </Tooltip>
                  <Tooltip
                    title={getStatusDescription(item.inTipalti, "IN TIPALTI")}
                  >
                    <StatusChip
                      label="IN TIPALTI"
                      style={{
                        backgroundColor: getColor(item.inTipalti),
                        color: getValueColor(item.inTipalti),
                      }}
                      aria-label={getStatusDescription(
                        item.inTipalti,
                        "IN TIPALTI"
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title={getStatusDescription(
                      item.isMatchedWithTipalti,
                      "MATCHED"
                    )}
                  >
                    <StatusChip
                      label="MATCHED"
                      style={{
                        backgroundColor: getColor(item.isMatchedWithTipalti),
                        color: getValueColor(item.isMatchedWithTipalti),
                      }}
                      aria-label={getStatusDescription(
                        item.isMatchedWithTipalti,
                        "MATCHED"
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title={getGroupPayeeIdsDescription(item.groupPayeeIds)}
                  >
                    <StatusChip
                      label={`GROUP (${
                        item.groupPayeeIds ? item.groupPayeeIds.length : 0
                      })`}
                      style={{
                        backgroundColor: getGroupPayeeIdsColor(
                          item.groupPayeeIds
                        ),
                        color: "#fff",
                      }}
                      aria-label={getGroupPayeeIdsDescription(
                        item.groupPayeeIds
                      )}
                    />
                  </Tooltip>
                </Box>
                {index < matchingItems.length - 1 && (
                  <Divider sx={{ borderBottomWidth: 3, my: 1 }} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </DialogStyled>
  );
}

export function PayeeDetailModal({
  payeeId,
  open,
  onClose,
  onBillRefCodeClick,
}: {
  payeeId: string | null;
  open: boolean;
  onClose: () => void;
  onBillRefCodeClick?: (billRefCode: string) => void;
}) {
  const [payeeData, setPayeeData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    async function getPayeeDetail() {
      if (!payeeId || !open) return;

      setLoading(true);
      setError(null);

      try {
        // Use the working /payees/{id} endpoint instead of /payees/GetPayeeDetails/{id}
        const response = await agent.Payees.getPayee(payeeId);
        setPayeeData(response);
      } catch (error) {
        console.error("Error fetching payee detail:", error);
        setError("Failed to load payee details. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    getPayeeDetail();
  }, [payeeId, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPayeeData(null);
      setError(null);
    }
  }, [open]);

  if (!payeeId) return null;

  return (
    <DialogStyled
      open={open}
      onClose={onClose}
      zIndex={1500}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "700px",
          width: "90%",
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography sx={{ color: theme.palette.text.primary }}>
              <LoadingSpinner />
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ py: 2 }}>
            <Typography color="error" variant="body1">
              {error}
            </Typography>
          </Box>
        )}

        {payeeData && !loading && !error && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Header with name and payable status */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {payeeData.name?.first} {payeeData.name?.middle}{" "}
                {payeeData.name?.last}
                <Chip
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography variant="body2">
                        {payeeData.payable ? "Payable" : "Not ACH Payable"}
                        {payeeData.reasonNotPayable && (
                          <span> ({payeeData.reasonNotPayable})</span>
                        )}
                      </Typography>
                      <Avatar sx={{ width: 16, height: 16 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                      </Avatar>
                    </Box>
                  }
                  color={payeeData.payable ? "success" : "warning"}
                />
              </Typography>
            </Box>

            {/* General Information */}
            <Accordion
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:before": {
                  backgroundColor: theme.palette.divider,
                },
              }}
            >
              <AccordionSummary
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      GENERAL
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      <strong>Payee ID:</strong> {payeeId}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      <strong>Type:</strong> {payeeData.type}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      <strong>Last 4:</strong> {payeeData.last4}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  General payee information from CSIPay database.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Contact Information */}
            <Accordion
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:before": {
                  backgroundColor: theme.palette.divider,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: theme.palette.text.primary }} />
                }
                aria-controls="panel2-content"
                id="panel2-header"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      CONTACT
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <strong>Name:</strong> {payeeData.name?.first}{" "}
                      {payeeData.name?.middle} {payeeData.name?.last}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      <strong>Email:</strong> {payeeData.email}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" paragraph>
                      <strong>Phone Numbers:</strong>
                      <br />
                      {payeeData.phone?.home && (
                        <>
                          Home: {payeeData.phone.home}
                          <br />
                        </>
                      )}
                      {payeeData.phone?.cell && (
                        <>Cell: {payeeData.phone.cell}</>
                      )}
                      {!payeeData.phone?.home && !payeeData.phone?.cell && (
                        <>No phone numbers on file</>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body2">
                      <strong>Mailing Address:</strong>
                      <br />
                      {payeeData.mailingAddress?.address1 && (
                        <>
                          {payeeData.mailingAddress.address1}{" "}
                          {payeeData.mailingAddress.address2}{" "}
                          {payeeData.mailingAddress.address3}
                          <br />
                          {payeeData.mailingAddress.city},{" "}
                          {payeeData.mailingAddress.state}{" "}
                          {payeeData.mailingAddress.postalCode}
                        </>
                      )}
                      {!payeeData.mailingAddress?.address1 &&
                        "No address on file"}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Payment Method */}
            <Accordion
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                "&:before": {
                  backgroundColor: theme.palette.divider,
                },
              }}
            >
              <AccordionSummary
                aria-controls="panel3-content"
                id="panel3-header"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Current Payment Method
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1">
                      {payeeData.paymentMethod || "Not specified"}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
            </Accordion>

            {/* Bills Summary */}
            {payeeData.bills && (
              <Accordion
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  "&:before": {
                    backgroundColor: theme.palette.divider,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: theme.palette.text.primary }}
                    />
                  }
                  aria-controls="panel4-content"
                  id="panel4-header"
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Bills
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Total:{" "}
                        {Array.isArray(payeeData.bills)
                          ? payeeData.bills.length
                          : 0}{" "}
                        bills
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {Array.isArray(payeeData.bills) &&
                    payeeData.bills.length > 0 ? (
                      payeeData.bills.map((bill: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            backgroundColor:
                              index % 2 === 0
                                ? theme.palette.background.default
                                : theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Typography variant="body2">
                                <strong>Application:</strong>{" "}
                                {bill.requestingApplication || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2">
                                <strong>Client:</strong>{" "}
                                {bill.requestingClient || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2">
                                <strong>Program:</strong>{" "}
                                {bill.programName || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2">
                                <strong>Invoice Date:</strong>{" "}
                                {bill.invoiceDate
                                  ? new Date(
                                      bill.invoiceDate
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Bill RefCode:</strong>
                                {onBillRefCodeClick &&
                                bill.paymentService?.billRefCode ? (
                                  <ClickableSpan
                                    onClick={() =>
                                      onBillRefCodeClick(
                                        bill.paymentService?.billRefCode
                                      )
                                    }
                                    aria-label="View bill details"
                                    style={{ marginLeft: "4px" }}
                                  >
                                    {bill.paymentService.billRefCode}
                                  </ClickableSpan>
                                ) : (
                                  <span style={{ marginLeft: "4px" }}>
                                    {bill.paymentService?.billRefCode || "N/A"}
                                  </span>
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2">
                                <strong>Bill Status:</strong>
                                <Chip
                                  label={bill.status || "Unknown"}
                                  size="small"
                                  color={
                                    bill.status === "Paid"
                                      ? "success"
                                      : bill.status === "Pending" ||
                                        bill.status === "PendingPayment"
                                      ? "warning"
                                      : "default"
                                  }
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2">
                                <strong>Bill Amount:</strong> $
                                {bill.amount?.toFixed(2) || "0.00"}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2">
                                <strong>Payment Status:</strong>
                                <Chip
                                  label={
                                    bill.paymentService?.paymentStatus ||
                                    "Unknown"
                                  }
                                  size="small"
                                  color={
                                    bill.paymentService?.paymentStatus ===
                                    "Paid"
                                      ? "success"
                                      : bill.paymentService?.paymentStatus ===
                                        "Pending"
                                      ? "warning"
                                      : "default"
                                  }
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No bills found for this payee.
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </DialogContent>
    </DialogStyled>
  );
}

export function BillDetailModal({
  billRefCode,
  open,
  onClose,
}: {
  billRefCode: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [billData, setBillData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function getBillDetail() {
      if (!billRefCode || !open) return;

      setLoading(true);
      setError(null);

      try {
        const response = await agent.Bills.getBillByRefCode(billRefCode);
        setBillData(response);
      } catch (error) {
        console.error("Error fetching bill detail:", error);
        setError("Failed to load bill details. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    getBillDetail();
  }, [billRefCode, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setBillData(null);
      setError(null);
    }
  }, [open]);

  if (!billRefCode) return null;

  const bill = billData?.bills?.[0];

  return (
    <DialogStyled
      open={open}
      onClose={onClose}
      zIndex={1600}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "800px",
          width: "90%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <LoadingSpinner />
          </Box>
        )}

        {error && (
          <Box sx={{ py: 2 }}>
            <Typography color="error" variant="body1">
              {error}
            </Typography>
          </Box>
        )}

        {billData && bill && !loading && !error && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Receipt Header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" gutterBottom>
                Bill #{billRefCode}
              </Typography>
              <Typography variant="body2" gutterBottom>
                This is the receipt for a payment of{" "}
                {bill.amount?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}{" "}
                (USD) made to {billData.name?.first} {billData.name?.last}.
              </Typography>
            </Box>

            {/* Payment Information */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption">Payment Ref Code</Typography>
                <Typography variant="body2">
                  {bill.paymentService?.paymentRefCode || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" align="right" display="block">
                  Payment Date
                </Typography>
                <Typography variant="body2" align="right">
                  {bill.paymentService?.paymentDate
                    ? new Intl.DateTimeFormat("en-US", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(new Date(bill.paymentService.paymentDate))
                    : "N/A"}
                </Typography>
              </Grid>
            </Grid>

            <Divider />

            {/* Payment To and Client Information */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption">Payment To:</Typography>
                <Typography variant="body2">
                  {billData.name?.first} {billData.name?.middle}{" "}
                  {billData.name?.last}
                  <br />
                  <br />
                  {bill.payTo && (
                    <>
                      {bill.payTo}
                      <br />
                    </>
                  )}
                  {bill.address?.address1} {bill.address?.address2}
                  <br />
                  {bill.address?.city}
                  <br />
                  {bill.address?.state}
                  <br />
                  {bill.address?.postalCode}
                  <br />
                  {billData.email && (
                    <Link href={`mailto:${billData.email}`}>
                      {billData.email}
                    </Link>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" align="right" display="block">
                  Client
                </Typography>
                <Typography variant="body2" align="right">
                  {bill.requestingApplication}
                  <br />
                  {bill.requestingClient}
                  <br />
                  {bill.programName}
                </Typography>
                <br />
                <Typography variant="caption" align="right" display="block">
                  Payment Type
                </Typography>
                <Typography variant="body2" align="right">
                  {bill.paymentMethod}
                </Typography>
                <Typography
                  variant="caption"
                  align="right"
                  display="block"
                  sx={{ mt: 2 }}
                >
                  Payment Status
                </Typography>
                <Typography variant="body2" align="right">
                  {bill.paymentService?.paymentStatus}
                </Typography>
              </Grid>
            </Grid>

            {/* Bill Details Table */}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Program</TableCell>
                  <TableCell />
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{bill.programName}</TableCell>
                  <TableCell />
                  <TableCell align="right">
                    {bill.amount?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell />
                  <TableCell>Total</TableCell>
                  <TableCell align="right">
                    {bill.amount?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Additional Bill Information */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Bill Status:</strong>{" "}
                    <Chip
                      label={bill.status || "Unknown"}
                      size="small"
                      color={
                        bill.status === "Paid"
                          ? "success"
                          : bill.status === "Pending" ||
                            bill.status === "PendingPayment"
                          ? "warning"
                          : "default"
                      }
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Invoice Date:</strong>{" "}
                    {bill.invoiceDate
                      ? new Date(bill.invoiceDate).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Grid>
                {bill.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Description:</strong> {bill.description}
                    </Typography>
                  </Grid>
                )}
                {bill.clientBillId && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Client Bill ID:</strong> {bill.clientBillId}
                    </Typography>
                  </Grid>
                )}
                {bill.applicantId && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Applicant ID:</strong> {bill.applicantId}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
    </DialogStyled>
  );
}
